import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/Seo";

const FAQS = [
  {
    q: "Is the fare really £100?",
    a: "Yes. Every accepted journey between our Essex service area and London costs exactly £100, in either direction. There are no zones, no mileage calculations and no hidden extras.",
  },
  {
    q: "Are there any extra charges?",
    a: "No. £100 is the full fare. No booking fees, no supplements, no card surcharges from us. You pay the driver £100 and nothing more.",
  },
  {
    q: "When do I pay?",
    a: "At the end of your journey, directly to your driver inside the Black Cab. We never take payment in advance and never ask for your card details online.",
  },
  {
    q: "Can I pay cash?",
    a: "Yes. Cash is welcomed — simply pay your driver £100 in the cab at the end of the journey.",
  },
  {
    q: "Can I pay by card?",
    a: "Yes. Drivers accept card payment in the cab using their own card payment terminal.",
  },
  {
    q: "Do I need to pay a deposit?",
    a: "No. There are no deposits, no advance payments and no card holds. The website never processes passenger payments of any kind.",
  },
  {
    q: "Is my journey guaranteed?",
    a: "No. Submitting a request does not guarantee a booking. All journeys are subject to driver availability and confirmation — please do not rely on a taxi being arranged until you receive confirmation from us.",
  },
  {
    q: "How will I know a driver has accepted?",
    a: "We will contact you using the phone number and email address you provide. Once a suitable driver has accepted, we confirm the booking with you directly.",
  },
  {
    q: "Can I request journeys in both directions?",
    a: "Yes. Essex → London (mainly early morning to late afternoon) and London → Essex (mainly early evening to late night). Drivers keep flexible schedules, so it is always worth asking.",
  },
  {
    q: "How far ahead should I request a journey?",
    a: "As early as you can. More notice gives us the best chance of matching your journey with a driver's existing travel plans. Short-notice requests are welcome but harder to fill.",
  },
  {
    q: "How many passengers can travel?",
    a: "Between 1 and 6 passengers, subject to the available vehicle's capacity. Tell us your party size on the request form.",
  },
  {
    q: "Can I bring luggage?",
    a: "Yes — London Black Cabs are spacious. Let us know on the form whether you have hand luggage, suitcases or bulky items so we can match you with a suitable cab.",
  },
  {
    q: "What if no driver is available?",
    a: "We will tell you honestly that the request could not be filled. You will never be charged anything, and you are free to make other arrangements.",
  },
  {
    q: "What happens if I need to cancel?",
    a: "Contact us as soon as possible using the details on the confirmation we send you. Because no payment is taken in advance, there is nothing to refund — we simply ask for as much notice as you can give, out of courtesy to the driver.",
  },
];

export default function Faqs() {
  return (
    <main data-testid="faqs-page" className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-20">
      <Seo
        title="FAQs — £100 Fixed Fare, Payment & Booking Questions"
        description="Answers about the £100 fixed fare, paying cash or card in the cab, deposits, confirmation, cancellations, passengers and luggage for Essex ↔ London Black Cab journeys."
      />
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">FAQs</p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
        Frequently asked questions
      </h1>
      <p className="mt-4 leading-relaxed text-[#A1A1AA]">
        Everything about the £100 fixed fare, how and when you pay, and what to expect after
        you request a journey.
      </p>

      <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
        {FAQS.map((f, i) => (
          <AccordionItem
            key={f.q}
            value={`faq-${i}`}
            className="border-[#27272A]"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger
              data-testid={`faq-trigger-${i}`}
              className="text-left text-base font-semibold text-white hover:text-[#D4AF37] hover:no-underline sm:text-lg"
            >
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="mt-12 text-sm text-[#A1A1AA]">
        Still unsure?{" "}
        <Link to="/contact" data-testid="faqs-contact-link" className="focus-gold text-[#D4AF37] underline-offset-4 hover:underline">
          Contact us
        </Link>{" "}
        or{" "}
        <Link to="/book" data-testid="faqs-book-link" className="focus-gold text-[#D4AF37] underline-offset-4 hover:underline">
          request your £100 journey
        </Link>
        .
      </p>
    </main>
  );
}
